import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import AnalysisLogsStream from "../components/AnalysisLogsStream";
import { analysisAPI } from "../services/api";

/**
 * AnalysisLogs
 *
 * Route: /analysis/:id/logs
 *
 * Log viewer page for a single analysis run (typically the R visualization step).
 * Renders the AnalysisLogsStream component for live/historical log output and
 * polls the analysis status API every 5 seconds to detect when the run finishes.
 *
 * Once the analysis status reaches "completed", a success banner appears with a
 * direct link to the results page (/analysis/:id/results).
 *
 * Navigation context is read from router location.state so the "back" button
 * returns to wherever the user came from (e.g. BatchAnalysisMonitor).
 *
 * @example
 * // Navigating here with context:
 * navigate(`/analysis/${vizId}/logs`, {
 *   state: { backTo: `/analysis/${parentId}/manage`, backLabel: 'Manage' }
 * });
 */
function AnalysisLogs() {
    const { id } = useParams();
    const location = useLocation();
    // Fall back to Dashboard if no navigation context was provided.
    const backTo = location.state?.backTo || '/dashboard';
    const backLabel = location.state?.backLabel || 'Dashboard';
    // True once the analysis status has been observed as "completed".
    // Used to show the "Analysis complete!" success banner with the results link.
    const [analysisStatusComplete, setAnalysisStatusComplete] = useState(false);
    // Poll the analysis status every 5 seconds.
    // Sets analysisStatusComplete to true as soon as the status is "completed".
    // The interval is cleared when the component unmounts.
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const response = await analysisAPI.getStatus(id);
                const { analysis_status } = response.data;
                if (analysis_status === 'completed' && !analysisStatusComplete) {
                    setAnalysisStatusComplete(true);
                }
            } catch (err) {
                console.error('Failed to check analysis status:', err);
            }
        };
        checkStatus();
        const interval = setInterval(checkStatus, 5000);

        return () => clearInterval(interval);
    }, [id, analysisStatusComplete]);

    return (
        <div className="p-8 max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center gap-4">
                <Link to={backTo}>
                    <Button variant="ghost" size="sm">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to {backLabel}
                    </Button>
                </Link>
            </div>

            <div>
                <h1 className="text-3xl font-bold mb-2">Logs</h1>
                <p className="text-muted-foreground">
                    Viewing logs for Analysis #{id}
                </p>
            </div>

            {/* Signal that analysis is complete */}
            {analysisStatusComplete && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-4 rounded-lg flex items-center gap-3">
                    <CheckCircle className="h-5 w-5" />
                    <span>Analysis complete!</span>

                    <div className="flex items-center gap-4">
                        <Link to={`/analysis/${id}/results`}>
                            <Button variant="ghost" size="sm">
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Results
                            </Button>
                        </Link>
                    </div>
                </div>

            )}

            {/* Logs Stream Component */}
            <AnalysisLogsStream analysisId={id} />
        </div>
    );
}

export default AnalysisLogs;
